import type { ReactElement } from "react";
import { HELP_STANDIN_NAME, type ColourRoleName } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * The chat scope icons (ADR-068's amendment of 2026-09-27; the design's `ScopeIcon`, a visual reference): each
 * chat scope's 8 by 8 glyph in its chat colour with a square 1 px outline on its sides and corners, as the design's
 * chained `drop-shadow`s draw it, original art painted into the UI atlas from `art/marks/chat/scope-<scope>_10.grid`
 * and drawn in the DOM by the atlas's generated rule. The image is 10 px square with its outline; `chat.css` lays it
 * out 10 across from the line's edge and 8 tall, its outline a pixel above and below, with a pixel of gap after it,
 * so every line starts at the log's edge and the text starts 11 in, a pixel clear of the outline as the design's is.
 *
 * An icon is a button whose accessible name is its scope's name (`chat.scopes` in the language files), and hovering, clicking,
 * tapping or focusing it shows that name in the kit's tooltip (the author's ruling of 2026-09-27; ADR-061, nothing
 * shown only on hover). The icon carries no handler of its own: the panel listens once on its body and finds the
 * icon an event came from by `data-chat-scope` (`scopeIconOf`), so rendering a log of icons makes no functions. A
 * log's icons stay out of the Tab order, as its names do; the entry row's is a Tab stop, the keyboard's path to it.
 */

/**
 * Every scope with an icon: the ten the design draws, and Help, whose icon is original art the design does not draw
 * (ADR-068's amendment "scope tabs, and Help's icon"). The page speaks Local, Whisper, Global, Trade and Help, and its
 * own lines are the system's; the rest wait for the rows that bring their scopes.
 */
export const CHAT_SCOPES = ["local", "region", "global", "trade", "help", "party", "whisper", "guild", "duel", "system", "broadcast"] as const;

/**
 * A scope with an icon.
 */
export type ChatScope = (typeof CHAT_SCOPES)[number];

/**
 * The id of the panel's one tooltip, which names a scope icon by its scope and a player's name in a line by its full
 * name (ADR-068's amendment "chat lines show the first name"); the id keeps the word it was given for the icons.
 */
export const SCOPE_TIP_ID = "ot-chat-scope-tip";

/**
 * The palette name a scope's icon is painted in: its chat colour's role, broadcast taking the system colour as the
 * design colours it, and Help its stand-in, which is no role (`odyssey-shared/src/ui/stand-ins.ts`).
 *
 * @param scope The scope.
 * @returns The colour role's name, or Help's stand-in's palette name.
 */
export function scopeIconColour(scope: ChatScope): ColourRoleName | typeof HELP_STANDIN_NAME {
  if (scope === "help") {
    return HELP_STANDIN_NAME;
  }
  return scope === "broadcast" ? "chat-system" : `chat-${scope}`;
}

/**
 * The atlas's class for a scope's icon.
 *
 * @param scope The scope.
 * @returns The class, `ot-ui-chat-scope-<scope>_10`.
 */
export function scopeIconClass(scope: ChatScope): string {
  return `ot-ui-chat-scope-${scope}_10`;
}

/**
 * Whether a string names a scope with an icon.
 *
 * @param value The string, such as an element's `data-chat-scope`.
 * @returns True for one of `CHAT_SCOPES`.
 */
export function isChatScope(value: string | undefined): value is ChatScope {
  return value !== undefined && (CHAT_SCOPES as readonly string[]).includes(value);
}

/**
 * The scope icon an event's target is or lies inside.
 *
 * @param target The event's target.
 * @returns The icon's element and scope, or null when the target is no icon.
 */
export function scopeIconOf(target: EventTarget | null): { readonly element: HTMLElement; readonly scope: ChatScope } | null {
  const element = target instanceof Element ? target.closest<HTMLElement>("[data-chat-scope]") : null;
  const scope = element?.dataset["chatScope"];
  return element !== null && element !== undefined && isChatScope(scope) ? { element, scope } : null;
}

/**
 * Where a scope's tooltip stands over its icon, in logical px of the box it is placed in: its left edge on the
 * icon's, and its foot a pixel above the icon's top, measured from the box's bottom edge.
 *
 * @param icon The icon's client rectangle.
 * @param box The positioned box's client rectangle.
 * @param px The device pixels a logical pixel takes.
 * @returns `left` from the box's left edge and `bottom` from its bottom edge, whole logical px.
 * @example
 * scopeTipPlace({ left: 14, top: 60 }, { left: 10, bottom: 100 }, 2);
 * // { left: 2, bottom: 21 }
 */
export function scopeTipPlace(icon: { readonly left: number; readonly top: number }, box: { readonly left: number; readonly bottom: number }, px: number): { readonly left: number; readonly bottom: number } {
  return { left: Math.round((icon.left - box.left) / px), bottom: Math.round((box.bottom - icon.top) / px) + 1 };
}

/**
 * A scope's icon.
 *
 * @param props The `scope`; whether it is a Tab stop (`focusable`), as the entry row's is; and whether its line
 *   names the scope in words of its own (`unnamed`), as a channel line and a whisper do off the screen, so the icon
 *   is hidden from assistive technology rather than read twice. Hover and click still show its name.
 * @returns The icon, a button named for its scope.
 * @example
 * <ScopeIcon scope="whisper" />
 */
export function ScopeIcon({ scope, focusable = false, unnamed = false }: { readonly scope: ChatScope; readonly focusable?: boolean; readonly unnamed?: boolean }): ReactElement {
  if (unnamed) {
    return <button type="button" className={`ot-chat__scope-icon ${scopeIconClass(scope)}`} aria-hidden="true" tabIndex={-1} data-chat-scope={scope} />;
  }
  return <button type="button" className={`ot-chat__scope-icon ${scopeIconClass(scope)}`} aria-label={say(`chat.scopes.${scope}`)} tabIndex={focusable ? 0 : -1} data-chat-scope={scope} />;
}
